'use client';

import { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { trackEvent, trackError, trackFormSubmit } from '@/lib/analytics/googleAnalytics';
import { createClient } from '@/lib/supabase/client';
import SessionExpiredBanner from './SessionExpiredBanner';

/**
 * Determine the correct post-auth destination for a user.
 * Checks onboarding_completed and organization_id from user_profiles.
 */
async function getPostAuthDestination(userId: string, fallback: string = '/executive-dashboard'): Promise<string> {
  try {
    const supabase = createClient();
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('onboarding_completed, organization_id')
      .eq('id', userId)
      .single();

    if (!profile || profile.onboarding_completed === false) {
      return '/5-step-onboarding-tutorial';
    }
    if (!profile.organization_id) {
      return '/organization-creation-onboarding';
    }
    return fallback.startsWith('/') ? fallback : '/executive-dashboard';
  } catch {
    return '/executive-dashboard';
  }
}

const TRUST_INDICATORS = [
  { label: 'ISO 42001', icon: '🛡️', desc: 'Certified Ready' },
  { label: 'SOC 2', icon: '🔒', desc: 'Type II Compliant' },
  { label: 'Federal-Ready', icon: '🏛️', desc: 'FedRAMP Aligned' },
];

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const masked = local.slice(0, 3) + '****';
  return `${masked}@${domain}`;
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [twoFAError, setTwoFAError] = useState('');
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const [totpSecondsLeft, setTotpSecondsLeft] = useState(30);
  const totpInputRef = useRef<HTMLInputElement>(null);
  const lockoutTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { signIn, signInWithGoogle } = useAuth();
  const router = useRouter();

  // TOTP 30-second window countdown
  useEffect(() => {
    if (!show2FA || useBackupCode) return;
    const tick = () => {
      const secs = 30 - (Math.floor(Date.now() / 1000) % 30);
      setTotpSecondsLeft(secs);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [show2FA, useBackupCode]);

  // Lockout countdown
  useEffect(() => {
    if (lockoutSeconds <= 0) return;
    lockoutTimerRef.current = setInterval(() => {
      setLockoutSeconds(s => {
        if (s <= 1) {
          clearInterval(lockoutTimerRef.current!);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => { if (lockoutTimerRef.current) clearInterval(lockoutTimerRef.current); };
  }, [lockoutSeconds]);

  // Auto-focus TOTP input when 2FA panel shows
  useEffect(() => {
    if (show2FA && !useBackupCode) {
      setTimeout(() => totpInputRef.current?.focus(), 300);
    }
  }, [show2FA, useBackupCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Sign in but intercept before redirect to check 2FA
      const supabase = createClient();
      const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });

      if (signInError) {
        const rawMessage = signInError.message.toLowerCase();

        let errorMessage: string;

        if (
          rawMessage.includes('invalid login credentials') ||
          rawMessage.includes('invalid credentials')
        ) {
          // Distinguish: check if the email exists as an OAuth-only identity
          const { data: identityCheck } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('email', email)
            .maybeSingle();

          if (!identityCheck) {
            // No profile at all — user does not exist
            errorMessage =
              'No account found with this email address. Please sign up or check your email.';
          } else {
            // Profile exists but password auth failed — likely OAuth-only or wrong password
            errorMessage =
              'Incorrect password. If you signed up with Google, please use the "Sign in with Google" button below. Otherwise, use "Forgot Password?" to reset your password.';
          }
        } else if (rawMessage.includes('email not confirmed')) {
          errorMessage =
            'Please verify your email address before signing in. Check your inbox for a confirmation link.';
        } else {
          errorMessage = signInError.message;
        }

        setError(errorMessage);
        trackEvent('login_failed', { page_path: '/login' });
        trackFormSubmit('login_form', 'failure', { error_message: errorMessage });
        trackError('authentication', errorMessage, 'LOGIN_FAILED', 'login_page');
        return;
      }

      if (!data.user) {
        setError('Authentication failed. Please try again.');
        return;
      }

      // Check if user has 2FA enabled
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('totp_enabled')
        .eq('id', data.user.id)
        .single();

      if (profile?.totp_enabled) {
        // Sign out the session — we'll complete it after 2FA
        await supabase.auth.signOut();
        setPendingUserId(data.user.id);
        setPendingEmail(data.user.email || email);
        setShow2FA(true);
        trackEvent('2fa_challenge_shown', { page_path: '/login' });
      } else {
        // No 2FA — complete login normally
        trackEvent('login_success', { page_path: '/login' });
        trackFormSubmit('login_form', 'success');
        // Respect the 'next' redirect param if present
        const params = new URLSearchParams(window.location.search);
        const nextPath = params.get('next');
        const destination = await getPostAuthDestination(data.user.id, nextPath && nextPath.startsWith('/') ? nextPath : '/executive-dashboard');
        router.push(destination);
      }
    } catch (err: any) {
      let errorMessage = err.message || 'Failed to sign in';
      setError(errorMessage);
      trackError('authentication', errorMessage, 'LOGIN_FAILED', 'login_page');
    } finally {
      setLoading(false);
    }
  };

  const handleTOTPChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setTotpCode(digits);
    if (digits.length === 6) {
      handleVerify2FA(digits, false);
    }
  };

  const handleVerify2FA = useCallback(async (codeOverride?: string, isBackup?: boolean) => {
    const code = isBackup ? backupCode : (codeOverride ?? totpCode);
    const isBackupCode = isBackup ?? useBackupCode;

    if (!code || (!isBackupCode && code.length !== 6)) return;
    if (lockoutSeconds > 0) return;

    setTwoFALoading(true);
    setTwoFAError('');

    try {
      const res = await fetch('/api/auth/2fa/challenge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, userId: pendingUserId, isBackupCode }),
      });
      const data = await res.json();

      if (data.locked) {
        setLockoutSeconds(data.secondsLeft || 900);
        setTwoFAError(data.error);
        return;
      }

      if (!data.verified) {
        setTwoFAError(data.error || 'Invalid code');
        setTotpCode('');
        return;
      }

      // 2FA verified — now sign in again to establish session
      const supabase = createClient();
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        setTwoFAError('Session error. Please go back and sign in again.');
        return;
      }

      trackEvent('2fa_verified', { page_path: '/login' });
      trackEvent('login_success', { page_path: '/login' });
      const destination2fa = await getPostAuthDestination(pendingUserId || '', '/executive-dashboard');
      router.push(destination2fa);
    } catch (err: any) {
      setTwoFAError(err.message || 'Verification failed');
    } finally {
      setTwoFALoading(false);
    }
  }, [backupCode, totpCode, useBackupCode, pendingUserId, email, password, lockoutSeconds, router]);

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (err: any) {
      setError(err.message || 'Failed to sign in with Google');
      setGoogleLoading(false);
    }
  };

  return (
    <div
      className="relative flex min-h-screen overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #020817 0%, #0F172A 40%, #0a1628 100%)' }}
    >
      {/* Particle/grid background */}
      <div
        className="pointer-events-none absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(201,169,97,0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }}
      />
      <div className="pointer-events-none absolute -left-40 top-1/4 h-96 w-96 rounded-full bg-[#C9A961]/5 blur-3xl" />
      <div className="pointer-events-none absolute -right-40 bottom-1/4 h-96 w-96 rounded-full bg-[#1E3A5F]/40 blur-3xl" />

      {/* LEFT SIDE — Branding */}
      <div className="relative hidden flex-col justify-between p-12 lg:flex lg:w-1/2">
        <div className="flex items-center gap-3">
          <div className="relative h-12 w-12 overflow-hidden rounded-xl border border-[#C9A961]/30 bg-[#1E3A5F]/60 p-1.5 shadow-lg">
            <Image
              src="/assets/images/Untitled_design-27-1772027045051.png"
              alt="ChronoMind logo"
              width={40}
              height={40}
              className="h-full w-full object-contain"
            />
          </div>
          <span className="font-heading text-xl font-bold text-white">
            Chrono<span className="text-[#C9A961]">Mind™</span>
          </span>
        </div>

        {/* Center content */}
        <div className="max-w-md">
          {/* Hex badge */}
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#C9A961]/30 bg-[#C9A961]/10 px-4 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[#C9A961]" />
            <span className="text-xs font-medium tracking-widest text-[#C9A961] uppercase">Executive Access Portal</span>
          </div>

          <h1 className="mb-4 font-heading text-4xl font-bold leading-tight text-white xl:text-5xl">
            Executive AI Governance{' '}
            <span className="bg-gradient-to-r from-[#C9A961] to-[#e8c97a] bg-clip-text text-transparent">
              Command Center
            </span>
          </h1>
          <p className="mb-10 text-lg leading-relaxed text-gray-400">
            Secure access to your enterprise AI governance platform. Monitor risks, track compliance, and drive accountability.
          </p>

          {/* Trust indicators */}
          <div className="grid grid-cols-3 gap-4">
            {TRUST_INDICATORS.map((item) => (
              <div
                key={item.label}
                className="rounded-xl border border-[#C9A961]/20 bg-[#1E3A5F]/30 p-4 text-center backdrop-blur-sm"
              >
                <div className="mb-1 text-2xl">{item.icon}</div>
                <div className="text-sm font-semibold text-[#C9A961]">{item.label}</div>
                <div className="text-xs text-gray-500">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom quote */}
        <div className="rounded-xl border border-white/5 bg-white/5 p-5 backdrop-blur-sm">
          <p className="text-sm italic text-gray-400">
            &ldquo;The only platform purpose-built for enterprise AI governance at federal scale.&rdquo;
          </p>
          <p className="mt-2 text-xs font-medium text-[#C9A961]">— ChronoMind™ Executive Team</p>
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div className="flex w-full flex-col items-center justify-center px-6 py-12 lg:w-1/2">
        {/* Mobile logo */}
        <div className="mb-8 flex items-center gap-3 lg:hidden">
          <div className="relative h-10 w-10 overflow-hidden rounded-lg border border-[#C9A961]/30 bg-[#1E3A5F]/60 p-1">
            <Image
              src="/assets/images/Untitled_design-27-1772027045051.png"
              alt="ChronoMind logo"
              width={32}
              height={32}
              className="h-full w-full object-contain"
            />
          </div>
          <span className="font-heading text-lg font-bold text-white">
            Chrono<span className="text-[#C9A961]">Mind™</span>
          </span>
        </div>

        <div className="w-full max-w-md">
          {/* Sliding panels container */}
          <div className="relative overflow-hidden">
            {/* LOGIN PANEL */}
            <div
              className="transition-all duration-500 ease-in-out"
              style={{
                transform: show2FA ? 'translateX(-110%)' : 'translateX(0)',
                opacity: show2FA ? 0 : 1,
                position: show2FA ? 'absolute' : 'relative',
                width: '100%',
              }}
            >
              <div
                className="rounded-2xl border border-[#C9A961]/20 p-8 shadow-2xl backdrop-blur-md"
                style={{ background: 'rgba(30, 58, 95, 0.4)' }}
              >
                <div className="mb-8">
                  <h2 className="font-heading text-2xl font-bold text-[#C9A961]">Welcome Back</h2>
                  <p className="mt-1 text-sm text-gray-400">Sign in to your governance platform</p>
                </div>

                {/* Session expired banner */}
                <Suspense fallback={null}>
                  <SessionExpiredBanner />
                </Suspense>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                      Email Address
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-gray-600 transition-all duration-200 focus:border-[#C9A961]/60 focus:bg-white/8 focus:outline-none focus:ring-2 focus:ring-[#C9A961]/20"
                      placeholder="you@organization.com"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <div className="mb-1.5 flex items-center justify-between">
                      <label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                        Password
                      </label>
                      <Link
                        href="/forgot-password"
                        className="text-xs font-medium text-[#C9A961] transition-colors hover:text-[#e8c97a] focus:outline-none"
                      >
                        Forgot Password?
                      </Link>
                    </div>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                        className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 pr-12 text-sm text-white placeholder-gray-600 transition-all duration-200 focus:border-[#C9A961]/60 focus:bg-white/8 focus:outline-none focus:ring-2 focus:ring-[#C9A961]/20"
                        placeholder="••••••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 transition-colors hover:text-gray-300 focus:outline-none"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Error */}
                  {error && (
                    <div className="flex items-start gap-2.5 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                      <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-xs text-red-400">{error}</p>
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="group relative w-full overflow-hidden rounded-lg bg-[#C9A961] px-6 py-3.5 text-sm font-bold text-[#0F172A] shadow-lg transition-all duration-300 hover:bg-[#e8c97a] hover:shadow-[0_0_20px_rgba(201,169,97,0.4)] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#C9A961] focus:ring-offset-2 focus:ring-offset-[#0F172A]"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Authenticating...
                      </span>
                    ) : (
                      'Sign In to Platform'
                    )}
                  </button>
                </form>

                {/* Sign up link */}
                <p className="mt-6 text-center text-sm text-gray-500">
                  Don&apos;t have an account?{' '}
                  <Link
                    href="/signup"
                    className="font-semibold text-[#C9A961] transition-colors hover:text-[#e8c97a] focus:outline-none focus:underline"
                  >
                    Sign Up
                  </Link>
                </p>

                {/* SSO Divider */}
                <div className="my-6 flex items-center gap-3">
                  <div className="h-px flex-1 bg-white/10" />
                  <span className="text-xs text-gray-600">Enterprise SSO</span>
                  <div className="h-px flex-1 bg-white/10" />
                </div>

                {/* SSO Buttons */}
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={googleLoading}
                    className="flex w-full items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-left text-sm transition-all duration-200 hover:border-[#4285F4]/40 hover:bg-[#4285F4]/5 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {googleLoading ? (
                      <svg className="h-5 w-5 flex-shrink-0 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    )}
                    <span className="font-medium text-gray-300">
                      {googleLoading ? 'Redirecting to Google...' : 'Sign in with Google'}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => window.location.href = '/sso-configuration-center'}
                    className="flex w-full items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-left text-sm transition-all duration-200 hover:border-[#007DC1]/40 hover:bg-[#007DC1]/5 focus:outline-none"
                  >
                    <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-[#007DC1] text-white text-xs font-bold">O</div>
                    <span className="font-medium text-gray-300">Sign in with Okta</span>
                    <span className="ml-auto text-xs text-gray-600">Enterprise</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => window.location.href = '/sso-configuration-center'}
                    className="flex w-full items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-left text-sm transition-all duration-200 hover:border-[#0078D4]/40 hover:bg-[#0078D4]/5 focus:outline-none"
                  >
                    <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-[#0078D4] text-white text-xs font-bold">A</div>
                    <span className="font-medium text-gray-300">Sign in with Azure AD</span>
                    <span className="ml-auto text-xs text-gray-600">Enterprise</span>
                  </button>
                </div>
              </div>
            </div>

            {/* 2FA CHALLENGE PANEL */}
            <div
              className="transition-all duration-500 ease-in-out"
              style={{
                transform: show2FA ? 'translateX(0)' : 'translateX(110%)',
                opacity: show2FA ? 1 : 0,
                position: show2FA ? 'relative' : 'absolute',
                width: '100%',
              }}
            >
              <div
                className="rounded-2xl border border-[#C9A961]/20 p-8 shadow-2xl backdrop-blur-md"
                style={{ background: 'rgba(30, 58, 95, 0.4)' }}
              >
                {/* Header */}
                <div className="mb-6 flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#C9A961]/30 bg-[#C9A961]/10">
                    <svg className="h-6 w-6 text-[#C9A961]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-[#C9A961]">Two-Factor Authentication</h2>
                    <p className="text-xs text-gray-500">Verifying identity for <span className="text-gray-300">{maskEmail(pendingEmail)}</span></p>
                  </div>
                </div>

                {lockoutSeconds > 0 ? (
                  <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-center">
                    <div className="mb-3 text-4xl font-mono font-bold text-red-400">
                      {Math.floor(lockoutSeconds / 60)}:{String(lockoutSeconds % 60).padStart(2, '0')}
                    </div>
                    <p className="text-sm font-semibold text-red-400">Account Temporarily Locked</p>
                    <p className="mt-1 text-xs text-gray-500">Too many failed attempts. Please wait before trying again.</p>
                  </div>
                ) : !useBackupCode ? (
                  <>
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-sm text-gray-300">Enter your 6-digit authenticator code</p>
                      <div className="flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1">
                        <div className={`h-1.5 w-1.5 rounded-full ${totpSecondsLeft <= 5 ? 'bg-red-400 animate-pulse' : totpSecondsLeft <= 10 ? 'bg-yellow-400' : 'bg-green-400'}`} />
                        <span className={`text-xs font-mono font-bold ${totpSecondsLeft <= 5 ? 'text-red-400' : totpSecondsLeft <= 10 ? 'text-yellow-400' : 'text-gray-400'}`}>{totpSecondsLeft}s</span>
                      </div>
                    </div>

                    <input
                      ref={totpInputRef}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={totpCode}
                      onChange={(e) => handleTOTPChange(e.target.value)}
                      placeholder="000000"
                      autoComplete="one-time-code"
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-4 text-center text-3xl font-mono font-bold tracking-[0.5em] text-white placeholder-gray-700 transition-all duration-200 focus:border-[#C9A961]/60 focus:outline-none focus:ring-2 focus:ring-[#C9A961]/20"
                    />

                    {twoFAError && (
                      <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                        <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <p className="text-xs text-red-400">{twoFAError}</p>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => handleVerify2FA()}
                      disabled={twoFALoading || totpCode.length !== 6}
                      className="mt-4 w-full rounded-lg bg-[#C9A961] px-6 py-3.5 text-sm font-bold text-[#0F172A] shadow-lg transition-all duration-300 hover:bg-[#e8c97a] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {twoFALoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                          Verifying...
                        </span>
                      ) : 'Verify'}
                    </button>

                    <button
                      type="button"
                      onClick={() => { setUseBackupCode(true); setTwoFAError(''); setTotpCode(''); }}
                      className="mt-3 w-full text-center text-xs text-[#C9A961] hover:text-[#e8c97a] transition-colors"
                    >
                      Use backup code instead
                    </button>
                  </>
                ) : (
                  <>
                    <p className="mb-3 text-sm text-gray-300">Enter your 8-character backup code</p>
                    <input
                      type="text"
                      value={backupCode}
                      onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                      placeholder="XXXXXXXX"
                      maxLength={8}
                      autoFocus
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-4 text-center text-xl font-mono font-bold tracking-widest text-white placeholder-gray-700 transition-all duration-200 focus:border-[#C9A961]/60 focus:outline-none focus:ring-2 focus:ring-[#C9A961]/20"
                    />

                    {twoFAError && (
                      <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                        <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <p className="text-xs text-red-400">{twoFAError}</p>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => handleVerify2FA(undefined, true)}
                      disabled={twoFALoading || backupCode.length < 6}
                      className="mt-4 w-full rounded-lg bg-[#C9A961] px-6 py-3.5 text-sm font-bold text-[#0F172A] shadow-lg transition-all duration-300 hover:bg-[#e8c97a] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {twoFALoading ? 'Verifying...' : 'Verify Backup Code'}
                    </button>

                    <button
                      type="button"
                      onClick={() => { setUseBackupCode(false); setTwoFAError(''); setBackupCode(''); }}
                      className="mt-3 w-full text-center text-xs text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      ← Use authenticator app instead
                    </button>
                  </>
                )}

                <button
                  type="button"
                  onClick={() => { setShow2FA(false); setPendingUserId(null); setTotpCode(''); setBackupCode(''); setTwoFAError(''); setLockoutSeconds(0); }}
                  className="mt-5 w-full text-center text-xs text-gray-600 hover:text-gray-400 transition-colors"
                >
                  ← Back to login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}